/**
 * Companion publishable site-key format validation.
 * Format: cmp_<tenantId>_<base32>
 */

import type { CompanionSiteKey } from './types';

/** Crockford/base32 alphabet (lowercase). */
const BASE32_PATTERN = /^[a-z2-7]+$/;

/** Tenant id: starts alphanumeric, then alphanumeric, underscore, hyphen. */
const TENANT_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,62}$/;

const SITE_KEY_PATTERN = /^cmp_([a-zA-Z0-9][a-zA-Z0-9_-]{0,62})_([a-z2-7]{8,32})$/;

export interface ParsedCompanionSiteKey {
  siteKey: CompanionSiteKey;
  tenantId: string;
  keySuffix: string;
}

export function isValidCompanionSiteKey(value: string | null | undefined): value is CompanionSiteKey {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const match = value.match(SITE_KEY_PATTERN);
  if (!match) {
    return false;
  }

  const [, tenantId, keySuffix] = match;
  return TENANT_ID_PATTERN.test(tenantId) && BASE32_PATTERN.test(keySuffix);
}

export function parseCompanionSiteKey(value: string): ParsedCompanionSiteKey | null {
  if (!isValidCompanionSiteKey(value)) {
    return null;
  }

  const match = value.match(SITE_KEY_PATTERN);
  if (!match) {
    return null;
  }

  const [, tenantId, keySuffix] = match;
  return {
    siteKey: value,
    tenantId,
    keySuffix
  };
}

export function readCompanionSiteKeyFromScript(
  currentScript: HTMLScriptElement | null
): CompanionSiteKey | null {
  const fromScript = currentScript?.getAttribute('data-camlin-companion-key');
  if (isValidCompanionSiteKey(fromScript)) {
    return fromScript;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const fallback = document.querySelector<HTMLScriptElement>(
    'script[data-camlin-companion-key]'
  );
  const fromDom = fallback?.getAttribute('data-camlin-companion-key');
  return isValidCompanionSiteKey(fromDom) ? fromDom : null;
}
